import { Component } from 'react';
import { Box, Stack, Typography } from 'components/base';
import { Logger } from 'utils/logger';
import ErrorImage from 'assets/img/error.svg';
import { primary, text } from 'assets/colors/colors';
import { Button } from 'components/atoms';
import { ErrorBoundaryProps, ErrorBoundaryState } from './types';

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    Logger.error({
      message: 'Error caught in Error Boundry: ',
      data: error,
      enableInProduction: true,
    });
    return { hasError: true };
  }

  componentDidCatch(_: Error, errorInfo: { componentStack: string }): void {
    Logger.error({
      message: 'Error Boundry Component Stack: ',
      data: errorInfo?.componentStack,
      enableInProduction: true,
    });
    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Stack
          justifyContent="center"
          width={this.props.width || '100%'}
          height={this.props.height || 'auto'}
          alignItems="center"
          marginTop="10px"
          overflow="auto"
        >
          <Box width="305px">
            <img src={ErrorImage} alt="error" width="100%" height="100%" />
          </Box>
          <Typography
            sx={{ color: primary.main, marginTop: 5, fontWeight: 500 }}
            variant="h5"
          >
            Oops! Something went wrong!
          </Typography>
          <Typography
            sx={{ color: text.primary, marginTop: 2, textAlign: 'center' }}
            variant="body1"
          >
            Looks like this page is having a little digital meltdown. 🫠 <br />
            Try refreshing to cool it down.
          </Typography>
          <Stack flexDirection="row" gap={1.5}>
            <Button
              variant="outlined"
              color="secondary"
              sx={{ borderRadius: '50px', marginTop: 2 }}
              onClick={() => {
                const { referrer } = document;
                const currentDomain = window?.location?.origin;
                if (referrer?.startsWith(currentDomain)) {
                  // Navigate to the previous page if it's within the varicon domain
                  window.history.back();
                } else {
                  // Otherwise, navigate to the homepage
                  window.location.href = '/';
                }
              }}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: '50px', marginTop: 2 }}
              onClick={() => window?.location?.reload()}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      );
    }

    return this.props.children;
  }
}
